// Takeoff command history — undo / redo for committed canvas edits.
//
// Every reversible edit is a COMMAND: { label, run, undo, redo? }. The history
// runs it and records it ONLY if it succeeded, so Undo can never offer to reverse
// an edit that was never saved. Rules, each one the fix for a defect the
// 2026-10 audit found in the previous stack:
//
//   - run first, record after: a command that fails (or throws CommandFailed
//     after reporting its own error) is not recorded;
//   - one gesture = one step: batch() runs several commands as ONE step, so a
//     group move is a single Ctrl+Z, and rolls back what it already did if a
//     later member fails;
//   - serialized: every execute / undo / redo goes through one queue, so two
//     quick Ctrl+Z presses undo two steps in order instead of racing on the same
//     step;
//   - undo targets what exists NOW: a command whose target is gone throws
//     CommandStale and is dropped, instead of leaving a stuck step on the stack;
//   - a failed undo / redo leaves the stack unchanged.
//
// Pure and framework-free; src/utils/useCommandHistory.js binds it to React.

export class CommandFailed extends Error {
  constructor(message = "Command failed") { super(message); this.name = "CommandFailed"; }
}
export class CommandStale extends Error {
  constructor(message = "That item no longer exists") { super(message); this.name = "CommandStale"; }
}

export const HISTORY_LIMIT = 50;

// command: { label, run: async () => any, undo: async () => any, redo?: async () => any }
// A command keeps whatever it needs between run / undo / redo in its own closure
// (for example the id a re-created row was given).
const redoOf = (cmd) => (typeof cmd.redo === "function" ? cmd.redo : cmd.run);
const valid = (cmd) => cmd && typeof cmd.run === "function" && typeof cmd.undo === "function";

export function createHistory({ limit = HISTORY_LIMIT } = {}) {
  let past = [];   // oldest -> newest ops
  let future = []; // most recently undone last
  let running = 0;
  let queue = Promise.resolve();
  let version = 0;
  let cached = null;
  const listeners = new Set();

  const touch = () => { version += 1; cached = null; listeners.forEach(l => l()); };
  const enqueue = (fn) => {
    running += 1; touch();
    const p = queue.then(fn);
    queue = p.then(() => {}, () => {});
    return p.finally(() => { running -= 1; touch(); });
  };
  const push = (op) => {
    past.push({ ...op, at: Date.now() });
    if (past.length > limit) past = past.slice(past.length - limit);
    future = [];
  };

  function getSnapshot() {
    if (cached && cached.version === version) return cached;
    const u = past[past.length - 1], r = future[future.length - 1];
    cached = {
      version, busy: running > 0,
      canUndo: past.length > 0, canRedo: future.length > 0,
      undoLabel: u ? u.label : null, redoLabel: r ? r.label : null,
      undoCount: past.length, redoCount: future.length,
    };
    return cached;
  }

  /** Run one command; resolves true when it ran and was recorded, false when it failed without throwing. */
  function execute(cmd) {
    if (!valid(cmd)) return Promise.resolve(false);
    return enqueue(async () => {
      try { await cmd.run(); }
      catch (e) { if (e instanceof CommandFailed) return false; throw e; }
      push({
        label: cmd.label,
        undo: () => cmd.undo(),
        redo: () => redoOf(cmd).call(cmd),
      });
      return true;
    });
  }

  /**
   * Run several commands as ONE step. If one fails, the ones already done are
   * undone (best effort) and nothing is recorded. Undoing / redoing the step later
   * skips members whose target no longer exists, and resumes where it stopped if one
   * member fails.
   */
  function batch(label, cmds) {
    const list = (cmds || []).filter(valid);
    if (!list.length) return Promise.resolve(false);
    return enqueue(async () => {
      const applied = list.map(() => false);
      for (let i = 0; i < list.length; i++) {
        try { await list[i].run(); applied[i] = true; }
        catch (e) {
          for (let j = i - 1; j >= 0; j--) { try { await list[j].undo(); } catch { /* best effort */ } }
          if (e instanceof CommandFailed) return false;
          throw e;
        }
      }
      push({
        label,
        undo: async () => {
          let stale = 0, did = 0;
          for (let j = list.length - 1; j >= 0; j--) {
            if (!applied[j]) continue;
            try { await list[j].undo(); applied[j] = false; did += 1; }
            catch (e) { if (e instanceof CommandStale) { applied[j] = false; stale += 1; } else throw e; }
          }
          if (stale && !did) throw new CommandStale("Everything in that step no longer exists");
        },
        redo: async () => {
          let stale = 0, did = 0;
          for (let j = 0; j < list.length; j++) {
            if (applied[j]) continue;
            try { await redoOf(list[j]).call(list[j]); applied[j] = true; did += 1; }
            catch (e) { if (e instanceof CommandStale) { stale += 1; } else throw e; }
          }
          if (stale && !did) throw new CommandStale("Everything in that step no longer exists");
        },
      });
      return true;
    });
  }

  /** Undo the newest step. Resolves { label } | { dropped, message } | null (nothing to undo, or it failed and was reported). */
  function undo() {
    return enqueue(async () => {
      const op = past[past.length - 1];
      if (!op) return null;
      try { await op.undo(); }
      catch (e) {
        if (e instanceof CommandStale) { past.pop(); return { dropped: true, message: e.message }; }
        if (e instanceof CommandFailed) return null;
        throw e;
      }
      past.pop(); future.push(op);
      return { label: op.label };
    });
  }

  function redo() {
    return enqueue(async () => {
      const op = future[future.length - 1];
      if (!op) return null;
      try { await op.redo(); }
      catch (e) {
        if (e instanceof CommandStale) { future.pop(); return { dropped: true, message: e.message }; }
        if (e instanceof CommandFailed) return null;
        throw e;
      }
      future.pop(); past.push(op);
      return { label: op.label };
    });
  }

  function clear() { past = []; future = []; touch(); }
  function subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); }

  return { execute, batch, undo, redo, clear, getSnapshot, subscribe };
}
